import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation, useOutletContext } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, UserCog, Tags, Plus, Trash2, Loader2, Palette, Check, ShoppingCart, Mail, Image, ImageOff, Paperclip, FileText, Save, CalendarRange, ShieldCheck, Video } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import MgmtUsers from "@/pages/admin/MgmtUsers";
import { useAuth } from "@/contexts/AuthContext";
import { PhotoUploader } from "@/components/PhotoUploader";
import { cacheAdminDatePreference } from "@/lib/adminDateRange";
import ManagementAccessUsers from "./ManagementAccessUsers";
import TutorialSettings from "./TutorialSettings";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LEDGER = "mgmt_usa_nj";

/**
 * Settings & tools page for the Management USA NJ admin — reached via the
 * gear icon (general settings) or the wrench icon (jumps straight to
 * Desktop) in the header (MgmtLayout). Holds the stuff that used to live
 * as extra tabs on the main Finance page (Users, Desktop) plus the
 * per-business Category manager and the color theme picker (moved out
 * of the header so the main page stays uncluttered).
 */
export default function MgmtSettings() {
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isDemo = user?.is_demo === true;
  const [accessPermission, setAccessPermission] = useState({ loading: true, allowed: false });
  useEffect(() => {
    let cancelled = false;
    setAccessPermission({ loading: true, allowed: false });
    api.get("/mgmt/access/capability").then(({ data }) => {
      if (!cancelled) setAccessPermission({ loading: false, allowed: data.can_manage === true });
    }).catch(() => { if (!cancelled) setAccessPermission({ loading: false, allowed: false }); });
    return () => { cancelled = true; };
  }, [user?.id]);
  const { theme, setThemePref } = useOutletContext() || {};
  const [section, setSection] = useState(location.pathname.endsWith("/tutorials") ? "tutorials" : location.pathname.endsWith("/access") ? "access" : location.state?.section === "desktop" && isDemo ? "categories" : location.state?.section || "categories");
  useEffect(() => { if (location.pathname.endsWith("/access")) setSection("access"); else if (location.pathname.endsWith("/tutorials")) setSection("tutorials"); }, [location.pathname]);
  const selectSection = value => {
    setSection(value);
    if (["access", "tutorials"].includes(value)) nav(`/managementusa/admin/settings/${value}`);
    else if (/\/(access|tutorials)$/.test(location.pathname)) nav("/managementusa/admin/settings", { state: { section: value } });
  };

  const sections = [
    ...(!isDemo ? [{ k: "dates", label: "Date display", icon: CalendarRange }] : []),
    { k: "invoices", label: "Invoices", icon: FileText },
    { k: "categories", label: "Categories", icon: Tags },
    { k: "shopping", label: "Shopping", icon: ShoppingCart },
    { k: "email", label: "Email", icon: Mail },
    { k: "users", label: accessPermission.allowed ? "Business users" : "Users", icon: UserCog },
    ...(accessPermission.allowed ? [{ k: "access", label: "Access control", icon: ShieldCheck }] : []),
    ...(accessPermission.allowed ? [{ k: "tutorials", label: "Video tutorials", icon: Video }] : []),
    { k: "appearance", label: "Appearance", icon: Palette },
  ];

  return (
    <div className="space-y-5 sm:space-y-6 min-w-0" data-testid="mgmt-settings-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" className="rounded-sm h-11 w-11 shrink-0" onClick={() => nav("/managementusa/admin")} title="Back to dashboard" data-testid="mgmt-settings-back">
          <ArrowLeft className="w-4 h-4" />
          <span className="sr-only">Back</span>
        </Button>
        <div>
          <p className="overline">Management workspace</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="mgmt-settings-heading">Settings</h1>
        </div>
      </div>

      <div className="sm:hidden" data-testid="mgmt-settings-mobile-nav"><Select value={sections.some(item => item.k === section) ? section : ""} onValueChange={selectSection}><SelectTrigger aria-label="Settings section" data-testid="mgmt-settings-section-select"><SelectValue placeholder="Settings section" /></SelectTrigger><SelectContent>{sections.map(({ k, label }) => <SelectItem key={k} value={k} data-testid={`mgmt-settings-mobile-${k}`}>{label}</SelectItem>)}</SelectContent></Select></div>
      <div className="hidden sm:block" data-testid="mgmt-settings-desktop-nav">
      <div className="management-settings-tabs flex flex-wrap gap-1 sm:gap-2 border-b border-border" data-testid="mgmt-settings-tabs">
        {sections.map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            aria-current={section === k ? "page" : undefined}
            onClick={() => selectSection(k)}
            className={`px-3 sm:px-4 py-2 min-h-11 text-sm font-semibold border-b-2 -mb-px transition-colors flex items-center gap-2 whitespace-nowrap snap-start ${section === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`mgmt-settings-tab-${k}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      </div>

      {section === "invoices" && <InvoiceProfileManager />}
      {section === "dates" && !isDemo && <DateDisplayManager />}
      {section === "categories" && <CategoryManager />}
      {section === "shopping" && <ShoppingPolicyManager />}
      {section === "email" && <EmailPolicyManager />}
      {section === "users" && <MgmtUsers />}
      {section === "access" && <ManagementAccessUsers allowed={accessPermission.allowed} permissionLoading={accessPermission.loading} />}
      {section === "tutorials" && <TutorialSettings allowed={accessPermission.allowed} permissionLoading={accessPermission.loading} initialAudience={location.state?.tutorialAudience || "admin"} />}
      {section === "desktop" && !isDemo && <Navigate to="/desktop" replace state={{ admin: true }} />}
      {section === "appearance" && <AppearanceManager theme={theme} setThemePref={setThemePref} />}
    </div>
  );
}

function DateDisplayManager() {
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/date-filter-settings")
      .then(({ data }) => setEnabled(data?.enabled !== false))
      .catch((error) => toast.error(formatErr(error.response?.data?.detail) || error.message))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (value) => {
    const previous = enabled;
    setEnabled(value);
    setSaving(true);
    try {
      await api.put("/date-filter-settings", { enabled: value });
      cacheAdminDatePreference(value);
      toast.success(value ? "Admin lists now open to the current calendar year" : "Admin lists now open to all history");
    } catch (error) {
      setEnabled(previous);
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally { setSaving(false); }
  };

  if (loading) return <div className="overline" data-testid="mgmt-date-settings-loading">Loading…</div>;
  return (
    <div className="space-y-4 max-w-2xl" data-testid="mgmt-date-settings">
      <div className="flex items-center gap-2">
        <CalendarRange className="w-5 h-5 text-primary" />
        <div><h2 className="font-display text-xl font-bold tracking-tight">Default date display</h2><p className="text-xs text-muted-foreground">Shared across both admin systems.</p></div>
      </div>
      <label className="flex items-start justify-between gap-4 border border-border rounded-sm p-4 cursor-pointer" data-testid="mgmt-date-current-year-label">
        <div><div className="font-medium text-sm">Open lists to the current calendar year</div><p className="text-xs text-muted-foreground mt-1">On: January 1 through December 31 of this year. Off: all history. Screen-level date controls remain available.</p></div>
        <Switch checked={enabled} disabled={saving} onCheckedChange={toggle} data-testid="mgmt-date-current-year-toggle" />
      </label>
    </div>
  );
}

const EMPTY_INVOICE_PROFILE = {
  display_name: "",
  invoice_email: "",
  phone: "",
  default_note: "",
  template: "legacy",
  header_background: "none",
  logo_data_url: "",
};

function InvoiceProfileManager() {
  const [form, setForm] = useState(EMPTY_INVOICE_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/mgmt/invoice-profile")
      .then((response) => setForm({ ...EMPTY_INVOICE_PROFILE, ...(response.data || {}) }))
      .catch((error) => toast.error(formatErr(error.response?.data?.detail) || error.message))
      .finally(() => setLoading(false));
  }, []);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const save = async () => {
    if (!form.display_name.trim()) return toast.error("Enter the name that should appear on invoices.");
    if (!form.invoice_email.includes("@")) return toast.error("Enter a valid invoice email.");
    setSaving(true);
    try {
      const response = await api.put("/mgmt/invoice-profile", {
        display_name: form.display_name,
        invoice_email: form.invoice_email,
        phone: form.phone,
        default_note: form.default_note,
        template: form.template,
        header_background: form.header_background,
        logo_data_url: form.logo_data_url,
      });
      setForm({ ...EMPTY_INVOICE_PROFILE, ...(response.data || {}) });
      toast.success("Your invoice settings were saved");
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-12 flex items-center justify-center text-muted-foreground" data-testid="mgmt-invoice-profile-loading"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invoice settings…</div>;
  return (
    <div className="max-w-5xl space-y-8" data-testid="mgmt-invoice-profile-manager">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-border pb-5">
        <div>
          <div className="flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /><h2 className="font-display text-xl font-bold tracking-tight">Your invoice identity</h2></div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Only Management invoices and payroll invoices you create use this identity. Pool Supply invoices are unchanged.</p>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-sm min-h-11" data-testid="mgmt-invoice-profile-save"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save invoice settings"}</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 items-start">
        <PhotoUploader value={form.logo_data_url} onChange={(value) => update("logo_data_url", value)} label="Invoice logo" testId="mgmt-invoice-logo" allowLive={false} previewFit="contain" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label htmlFor="mgmt-invoice-display-name">Name on invoices</Label><Input id="mgmt-invoice-display-name" value={form.display_name} onChange={(event) => update("display_name", event.target.value)} maxLength={100} data-testid="mgmt-invoice-display-name" /></div>
          <div><Label htmlFor="mgmt-invoice-email">Invoice email</Label><Input id="mgmt-invoice-email" type="email" value={form.invoice_email} onChange={(event) => update("invoice_email", event.target.value)} data-testid="mgmt-invoice-email" /><p className="text-[11px] text-muted-foreground mt-1">Starts with your account email. Editing it does not change sign-in.</p></div>
          <div className="sm:col-span-2"><Label htmlFor="mgmt-invoice-phone">Phone number</Label><Input id="mgmt-invoice-phone" type="tel" value={form.phone} onChange={(event) => update("phone", event.target.value)} maxLength={40} placeholder="(555) 555-0123" data-testid="mgmt-invoice-phone" /></div>
          <div className="sm:col-span-2"><Label htmlFor="mgmt-invoice-default-note">Note on every invoice</Label><Textarea id="mgmt-invoice-default-note" value={form.default_note} onChange={(event) => update("default_note", event.target.value)} maxLength={700} rows={3} placeholder="Payment terms, thank-you message, or instructions" data-testid="mgmt-invoice-default-note" /><div className="text-[10px] text-right text-muted-foreground mt-1" data-testid="mgmt-invoice-note-count">{form.default_note.length}/700</div></div>
        </div>
      </div>

      <div className="space-y-3 border-t border-border pt-6">
        <div><h3 className="font-display text-lg font-bold">Invoice design</h3><p className="text-xs text-muted-foreground">Your selection is locked into each new invoice, including payroll invoices.</p></div>
        <div className="flex items-start justify-between gap-4 rounded-sm border border-border bg-muted/30 p-3" data-testid="mgmt-invoice-background-row">
          <div>
            <Label htmlFor="mgmt-invoice-background" className="text-sm font-semibold">Light blue header background</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5 max-w-md">Adds the soft blue banner across the top of the page — the same look as your current website invoice. Shown on the Current and Architect layouts.</p>
          </div>
          <Switch id="mgmt-invoice-background" checked={form.header_background === "sky"} onCheckedChange={(on) => update("header_background", on ? "sky" : "none")} data-testid="mgmt-invoice-background-toggle" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5" role="radiogroup" aria-label="Invoice template" data-testid="mgmt-invoice-template-options">
          {["architect", "executive", "innovator", "legacy"].map((template) => <InvoiceTemplateOption key={template} template={template} background={form.header_background} selected={form.template === template} onSelect={() => update("template", template)} />)}
        </div>
      </div>
    </div>
  );
}

function InvoiceTemplateOption({ template, background, selected, onSelect }) {
  const labels = { architect: "The Architect", executive: "The Executive", innovator: "The Innovator", legacy: "Management USA (Current)" };
  const descriptions = { architect: "Structured grid", executive: "Corporate luxury", innovator: "Bright and modern", legacy: "Your current layout" };
  return (
    <button type="button" role="radio" aria-checked={selected} onClick={onSelect} className={`text-left border rounded-sm p-3 transition-[border-color,background-color,transform] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`} data-testid={`mgmt-invoice-template-${template}`}>
      <div className="flex items-start justify-between gap-2 mb-2"><div><span className="font-semibold text-sm block">{labels[template]}</span><span className="text-[10px] text-muted-foreground">{descriptions[template]}</span></div>{selected && <Check className="w-4 h-4 text-primary shrink-0" data-testid={`mgmt-invoice-template-${template}-selected`} />}</div>
      <InvoiceTemplatePreview template={template} background={background} />
    </button>
  );
}

function InvoiceTemplatePreview({ template, background }) {
  if (template === "architect") return <ArchitectInvoicePreview background={background} />;
  if (template === "executive") return <ExecutiveInvoicePreview />;
  if (template === "innovator") return <InnovatorInvoicePreview />;
  return <LegacyInvoicePreview background={background} />;
}

const SkyBand = () => (
  <div className="absolute inset-x-0 top-0 h-[18%] pointer-events-none" style={{ background: "linear-gradient(to bottom, #cfe0f2, #ffffff)" }} data-testid="mgmt-invoice-preview-sky" />
);

function PreviewRows({ light = false }) {
  return (
    <div className="space-y-2 mt-2">{[1, 2, 3].map((row) => <div key={row} className={`flex justify-between border-b pb-1.5 ${light ? "border-white/25" : "border-neutral-200"}`}><div className={`h-1 w-2/3 ${light ? "bg-white/45" : "bg-neutral-300"}`} /><div className={`h-1 w-1/5 ${light ? "bg-white/65" : "bg-neutral-400"}`} /></div>)}</div>
  );
}

function ArchitectInvoicePreview({ background }) {
  return (
    <div className="relative aspect-[8.5/11] bg-white border border-neutral-300 overflow-hidden p-4" style={{ backgroundImage: "linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)", backgroundSize: "18px 18px" }} aria-label="Architect invoice example" data-testid="mgmt-invoice-template-architect-preview">
      {background === "sky" && <SkyBand />}
      <div className="absolute inset-y-0 left-0 w-2 bg-neutral-950" />
      <div className="relative bg-white/90 p-2 border-l-4 border-neutral-950"><div className="text-[11px] font-black tracking-tight">INVOICE / 0248</div><div className="text-[5px] uppercase tracking-[0.22em] mt-1">Structured management billing</div></div>
      <div className="relative mt-6 flex justify-between"><div className="text-[6px] font-bold uppercase">Bill to<br/><span className="font-normal">Bungalow Colony</span></div><div className="text-right text-[6px] font-mono">08.25.26<br/>DUE 09.10.26</div></div>
      <div className="relative mt-6 border-t-2 border-neutral-950 pt-1 text-[5px] font-bold uppercase flex justify-between"><span>Description</span><span>Amount</span></div><PreviewRows />
      <div className="relative mt-6 bg-neutral-950 text-white px-2 py-2 flex justify-between text-[7px] font-black"><span>TOTAL DUE</span><span>$4,285.00</span></div>
    </div>
  );
}

function ExecutiveInvoicePreview() {
  return (
    <div className="relative aspect-[8.5/11] bg-[#f8faf9] border border-[#cbd5d1] overflow-hidden" aria-label="Executive invoice example" data-testid="mgmt-invoice-template-executive-preview">
      <div className="h-[34%] bg-[#173f35] text-white p-4 relative overflow-hidden"><div className="absolute w-24 h-24 rounded-full border border-white/15 -right-8 -top-8" /><div className="absolute w-16 h-16 rounded-full border border-white/10 right-7 top-10" /><div className="relative text-[5px] uppercase tracking-[0.25em] text-white/60">Management services</div><div className="relative font-serif text-xl mt-2">Invoice</div><div className="relative text-[7px] mt-5">Your Business Name</div><div className="relative text-[5px] text-white/60">billing@example.com · (555) 555-0123</div></div>
      <div className="p-4"><div className="flex justify-between"><div className="text-[6px] text-[#173f35] font-bold">BILL TO<br/><span className="font-normal text-neutral-500">Bungalow Colony</span></div><div className="font-serif text-sm text-[#173f35]">#0248</div></div><div className="mt-5 bg-[#173f35] text-white h-4 px-2 flex items-center justify-between text-[5px] uppercase"><span>Services</span><span>Amount</span></div><PreviewRows /><div className="mt-6 border-t border-[#173f35] pt-2 flex justify-between font-serif text-[9px] text-[#173f35]"><span>Total</span><span>$4,285.00</span></div></div>
    </div>
  );
}

function InnovatorInvoicePreview() {
  return (
    <div className="relative aspect-[8.5/11] bg-[#eef8ff] border border-sky-200 overflow-hidden p-4" aria-label="Innovator invoice example" data-testid="mgmt-invoice-template-innovator-preview">
      <div className="absolute -right-14 -top-10 w-36 h-36 rounded-full bg-sky-500" /><div className="absolute -right-4 top-20 w-16 h-16 rounded-full bg-cyan-300" /><div className="absolute -left-12 bottom-8 w-24 h-24 rounded-full border-[12px] border-sky-200" />
      <div className="relative bg-white border border-sky-100 shadow-sm p-3"><div className="flex justify-between"><div><div className="text-[5px] uppercase tracking-[0.2em] text-sky-600">Invoice</div><div className="text-[12px] font-black text-sky-950">Your Business</div></div><div className="text-right font-mono text-[7px] text-sky-700">#0248<br/><span className="text-[5px]">AUG 25</span></div></div></div>
      <div className="relative mt-4 bg-white/90 border border-sky-100 p-3"><div className="flex justify-between text-[6px]"><div><span className="text-sky-500 font-bold">TO</span><br/>Bungalow Colony</div><div className="text-right"><span className="text-sky-500 font-bold">STATUS</span><br/><span className="inline-block bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full">OPEN</span></div></div><div className="mt-5 bg-sky-600 h-1" /><PreviewRows /><div className="mt-5 bg-sky-950 text-white p-2 flex justify-between text-[8px] font-bold"><span>Total due</span><span>$4,285.00</span></div></div>
    </div>
  );
}

function LegacyInvoicePreview({ background }) {
  return (
    <div className="relative aspect-[8.5/11] bg-white border border-neutral-300 overflow-hidden p-4" aria-label="Current Management USA invoice example" data-testid="mgmt-invoice-template-legacy-preview">
      {background === "sky" && <SkyBand />}
      <div className="relative flex justify-between items-start"><div><div className="text-[9px] font-black text-[#0b2447]">MANAGEMENT</div><div className="text-[5px] tracking-[0.25em] text-[#0b2447]">USA · NJ</div></div><div className="text-right"><div className="text-[7px] font-bold text-[#0b2447]">INVOICE</div><div className="text-[6px] font-mono">#0248</div></div></div><div className="relative h-1 bg-[#0b2447] mt-3" />
      <div className="flex justify-between mt-4 text-[6px]"><div><b>BILL TO</b><br/>Bungalow Colony</div><div className="text-right">Invoice date: 08/25/26<br/>Status: DRAFT</div></div><div className="mt-5 bg-[#0b2447] text-white h-4 px-2 flex items-center justify-between text-[5px]"><span>DATE / DESCRIPTION</span><span>AMOUNT</span></div><PreviewRows /><div className="mt-6 border-t-2 border-[#0b2447] pt-2 flex justify-between text-[8px] font-bold text-[#0b2447]"><span>TOTAL DUE</span><span>$4,285.00</span></div><div className="mt-7 text-center text-[5px] text-neutral-400">Management USA NJ</div>
    </div>
  );
}

function EmailPolicyManager() {
  const [mode, setMode] = useState("off");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/mgmt/ticket-email-photo-policy")
      .then((response) => setMode(response.data?.mode || (response.data?.embed_photos ? "inline" : "off")))
      .catch((error) => toast.error(formatErr(error.response?.data?.detail) || error.message))
      .finally(() => setLoading(false));
  }, []);

  const saveMode = async (value) => {
    const previous = mode;
    setMode(value);
    setSaving(true);
    try {
      await api.put("/mgmt/ticket-email-photo-policy", { mode: value });
      toast.success(value === "inline" ? "Ticket photos will appear inside emails" : value === "attachment" ? "Original ticket photos will be attached" : "Ticket emails will not include photo files");
    } catch (error) {
      setMode(previous);
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally {
      setSaving(false);
    }
  };

  const options = [
    { value: "off", label: "Off", description: "Email text only. Photos remain in the portal.", icon: ImageOff },
    { value: "inline", label: "Inside email", description: "Photos appear directly below the ticket update.", icon: Image },
    { value: "attachment", label: "As attachments", description: "Original photo files are attached to the email.", icon: Paperclip },
  ];

  if (loading) return <div className="overline" data-testid="mgmt-email-policy-loading">Loading…</div>;
  return (
    <div className="space-y-4 max-w-2xl" data-testid="mgmt-email-policy-manager">
      <div className="flex items-center gap-2">
        <Mail className="w-5 h-5 text-primary" />
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight">Ticket email photos</h2>
          <p className="text-xs text-muted-foreground">Applies to your office, workers, and colony contacts.</p>
        </div>
      </div>
      <div>
        <div className="font-medium text-sm">How new photos appear</div>
        <p className="text-xs text-muted-foreground mt-1">Applies only to photos added in that new ticket, reply, update, or closure. Earlier photos are not repeated.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup" aria-label="Ticket email photo delivery" data-testid="mgmt-email-photo-policy-options">
        {options.map(({ value, label, description, icon: Icon }) => {
          const selected = mode === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={saving}
              onClick={() => saveMode(value)}
              className={`text-left border rounded-sm p-3 transition-[border-color,background-color,transform] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
              data-testid={`mgmt-email-photo-mode-${value}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 font-semibold text-sm"><Icon className="w-4 h-4 text-primary" />{label}</span>
                {selected && <Check className="w-4 h-4 text-primary" data-testid={`mgmt-email-photo-mode-${value}-selected`} />}
              </div>
              <p className="text-xs text-muted-foreground mt-1 min-h-8">{description}</p>
              <EmailPhotoExample mode={value} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EmailPhotoExample({ mode }) {
  return (
    <div className="mt-3 border-t border-border pt-2 space-y-1.5" aria-label={`${mode} email example`} data-testid={`mgmt-email-photo-example-${mode}`}>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Example</div>
      <div className="h-1.5 w-3/4 bg-muted-foreground/25 rounded-sm" /><div className="h-1.5 w-1/2 bg-muted-foreground/20 rounded-sm" />
      {mode === "inline" && <div className="aspect-[16/8] bg-secondary border border-border rounded-sm flex items-center justify-center"><Image className="w-5 h-5 text-muted-foreground" /></div>}
      {mode === "attachment" && <div className="flex items-center gap-1.5 text-[10px] border border-border rounded-sm px-2 py-1.5 bg-secondary/30"><Paperclip className="w-3 h-3" /><span className="truncate">ticket-photo-1.jpg</span></div>}
      {mode === "off" && <div className="text-[10px] text-muted-foreground pt-1">Open the portal to view photos</div>}
    </div>
  );
}

function ShoppingPolicyManager() {
  const [required, setRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/mgmt/shopping-dropoff-policy")
      .then((r) => setRequired(!!r.data?.required))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (v) => {
    setRequired(v);
    setSaving(true);
    try {
      await api.put("/mgmt/shopping-dropoff-policy", { required: v });
      toast.success(v ? "Drop-off photo is now REQUIRED" : "Drop-off photo is now optional");
    } catch (e) {
      setRequired(!v);
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="overline">Loading…</div>;
  return (
    <div className="space-y-4 max-w-2xl" data-testid="mgmt-shopping-policy-manager">
      <div className="flex items-center gap-2">
        <ShoppingCart className="w-5 h-5 text-primary" />
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight">Drop-off photo</h2>
          <p className="text-xs text-muted-foreground">Applies to every colony in your business.</p>
        </div>
      </div>
      <label className="flex items-start justify-between gap-4 border border-border rounded-sm p-4 cursor-pointer">
        <div>
          <div className="font-medium text-sm">Require a drop-off photo after purchase</div>
          <p className="text-xs text-muted-foreground mt-1">
            When ON, a purchased item shows a highlighted reminder until the buyer attaches a photo of
            where they dropped it off (via the camera on the item). When OFF, the drop-off photo is
            optional. Either way, the photo shows on the ticket next to the product request.
          </p>
        </div>
        <Switch checked={required} disabled={saving} onCheckedChange={toggle} data-testid="mgmt-dropoff-required-switch" />
      </label>
    </div>
  );
}

function AppearanceManager({ theme, setThemePref }) {
  const options = [
    { k: "blue", label: "Blue", desc: "The default slate & sky-blue look.", swatch: "bg-slate-900" },
    { k: "green", label: "Green", desc: "Bungalow-matched emerald look.", swatch: "bg-emerald-600" },
  ];
  return (
    <div className="space-y-4 max-w-2xl" data-testid="mgmt-appearance-manager">
      <p className="text-sm text-muted-foreground">
        Pick the color theme for your Management USA NJ portal. Your choice is saved to your
        account and follows you to any device you sign in on.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((o) => {
          const active = (theme || "blue") === o.k;
          return (
            <button
              key={o.k}
              type="button"
              onClick={() => setThemePref && setThemePref(o.k)}
              className={`text-left border rounded-sm p-4 transition ${active ? "border-primary ring-1 ring-primary" : "border-border hover:bg-secondary/40"}`}
              data-testid={`mgmt-appearance-option-${o.k}`}
            >
              <div className="flex items-center justify-between">
                <span className={`w-6 h-6 rounded-full ${o.swatch}`} />
                {active && <Check className="w-4 h-4 text-primary" data-testid={`mgmt-appearance-active-${o.k}`} />}
              </div>
              <div className="font-semibold mt-2">{o.label}</div>
              <p className="text-xs text-muted-foreground mt-0.5">{o.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CategoryManager() {
  const [custom, setCustom] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/expenses/categories", { params: { ledger: LEDGER } })
      .then((r) => {
        setCustom(r.data?.custom || []);
        setDefaults(r.data?.categories?.filter((c) => !(r.data?.custom || []).some((x) => x.name === c)) || []);
      })
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const addCategory = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Enter a category name.");
    setAdding(true);
    try {
      await api.post("/expenses/categories", { name, ledger: LEDGER });
      setNewName("");
      toast.success(`"${name}" added to the Category dropdown.`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setAdding(false);
    }
  };

  const removeCategory = async (c) => {
    setBusyId(c.id);
    try {
      await api.delete(`/expenses/categories/${c.id}`, { params: { ledger: LEDGER } });
      setCustom((prev) => prev.filter((x) => x.id !== c.id));
      toast.success(`"${c.name}" removed.`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="mgmt-category-manager">
      <p className="text-sm text-muted-foreground">
        Categories you add here show up in the expense Category dropdown for your business only —
        other Management USA NJ businesses on this platform never see them.
      </p>
      <div className="flex gap-2 mobile-stack">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addCategory(); }}
          placeholder="e.g. Snow removal"
          className="rounded-sm h-9"
          data-testid="mgmt-category-new-input"
        />
        <Button size="sm" className="rounded-sm mobile-full" onClick={addCategory} disabled={adding} data-testid="mgmt-category-add-btn">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Add
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Your custom categories</p>
            {custom.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="mgmt-category-custom-empty">None added yet — type one above.</p>
            ) : (
              <ul className="divide-y divide-border border border-border rounded-sm">
                {custom.map((c) => (
                  <li key={c.id} className="px-3 py-2 flex items-center justify-between text-sm" data-testid={`mgmt-category-row-${c.id}`}>
                    {c.name}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-11 w-11 sm:h-7 sm:w-7 p-0 text-destructive"
                      onClick={() => removeCategory(c)}
                      disabled={busyId === c.id}
                      data-testid={`mgmt-category-delete-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Built-in defaults</p>
            <div className="flex flex-wrap gap-1.5">
              {defaults.map((c) => <Badge key={c} variant="secondary" className="rounded-sm">{c}</Badge>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
